import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import 'dayjs/locale/fr';

import { Button, Icon } from '@mairistem/composants';

import { REGISSEUR } from '@/constants';
import { RegieContext } from '@/contexts';
import { useRegieStore, useRegisseurStore } from '@/zustand';
import TileBase from './TileBase';

dayjs.locale('fr');

const MANDAT_LIBELLES: Record<string, string> = {
  [REGISSEUR.TYPE_PRINCIPAL]: 'régisseur titulaire',
  [REGISSEUR.TYPE_SUPPLEANT]: 'régisseur suppléant',
  [REGISSEUR.TYPE_AGENT]: 'agent de guichet',
};

const TileNomRegie = () => {
  const regie = useRegieStore((state) => state.regie);
  const regisseur = useRegisseurStore((state) => state.regisseur);
  const { showList, regies } = React.useContext(RegieContext);

  const jourEnCours = dayjs().format('dddd D MMMM YYYY');

  // Récupère le nom du régisseur depuis les régisseurs de la régie
  const regisseurInfo = React.useMemo(() => {
    if (!regisseur || !regies) return null;

    const regieComplete = _.find(regies, { identifiant: regie?.identifiant });
    if (!regieComplete?.regisseurs) return null;

    const regisseurData = _.find(regieComplete.regisseurs, {
      identifiant: regisseur.identifiantRegisseur,
    });

    if (!regisseurData) return null;

    return {
      nom: `${regisseurData.prenom} ${regisseurData.nom}`,
      statut: MANDAT_LIBELLES[regisseurData.mandat] ?? regisseurData.mandat,
    };
  }, [regisseur, regies, regie?.identifiant]);

  // Détermine le statut de la régie (test ou production)
  const statutRegie = React.useMemo(() => {
    if (regie?.ecole) {
      return {
        isTest: true,
        dateProduction: regie?.dateProduction
          ? `(Régie mise en production le ${dayjs(regie.dateProduction).format('DD/MM/YYYY')})`
          : null,
      };
    }
    return {
      isTest: false,
      dateProduction: regie?.dateProduction
        ? `Régie mise en production le ${dayjs(regie.dateProduction).format('DD/MM/YYYY')}`
        : 'Régie en production',
    };
  }, [regie?.ecole, regie?.dateProduction]);

  return (
    <TileBase className="tile-nom-regie">
      {/* Header: Titre */}
      <div className="tile-nom-regie-header">
        <span className="tile-nom-regie-titre">{regie?.titre}</span>
        {/* Bouton changer de régie */}
        {_.size(regies) > 1 && (
          <Button
            className="domain-colored"
            onClick={showList}
            basic
            icon="exchange"
            circular
            style={{ marginLeft: '1em' }}
            title="Changer de régie"
          />
        )}
      </div>

      {/* Date du jour */}
      <div className="tile-nom-regie-date">{jourEnCours}</div>

      {/* Régisseur connecté */}
      {regisseurInfo && (
        <div className="tile-nom-regie-regisseur">
          <span className="regisseur-bonjour">Bonjour </span>
          <span className="regisseur-nom">{regisseurInfo.nom}</span>
          <span className="regisseur-statut">{`, ${regisseurInfo.statut}.`}</span>
        </div>
      )}

      {/* Statut test/prod en bas */}
      <div className="tile-nom-regie-footer">
        {statutRegie.isTest && (
          <span className="tile-nom-regie-test">
            <Icon name="FlaskConical" iconSet="Lucide" className="colored" />
            Régie test
          </span>
        )}
        {statutRegie.dateProduction && (
          <span className="tile-nom-regie-production">{statutRegie.dateProduction}</span>
        )}
      </div>
    </TileBase>
  );
};

export default TileNomRegie;
